<script setup>
import { CalendarIcon } from '@heroicons/vue/20/solid';
import { TagIcon, MapPinIcon } from '@heroicons/vue/24/outline';
import { computed } from 'vue';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';

const props = defineProps({
    topics: {
        type: Array,
        required: false,
        default: () => [],
    },
    contributors: {
        type: Array,
        required: false,
        default: () => [],
    },
    date: {
        type: String,
        required: false,
        default: null,
    },
    startDate: {
        type: String,
        required: false,
        default: null,
    },
    endDate: {
        type: String,
        required: false,
        default: null,
    },
    repetitionDatesCount: {
        type: Number,
        required: false,
        default: null,
    },
    location: {
        type: String,
        required: false,
        default: null,
    },
    isHero: {
        type: Boolean,
        default: false,
    },
    isVertical: {
        type: Boolean,
        default: false,
    },
    isInactive: {
        type: Boolean,
        default: false,
    },
});

const textColorClass = computed(() => {
    switch (true) {
        case props.isInactive: {
            return 'text-gray-500';
        }

        case props.isHero: {
            return 'text-white';
        }

        default: {
            return 'text-gray-800';
        }
    }
});

const iconColorClass = computed(() => {
    switch (true) {
        case props.isInactive: {
            return 'text-gray-400';
        }

        case props.isHero: {
            return 'text-gray-200';
        }

        default: {
            return 'text-gray-500';
        }
    }
});
</script>

<template>
    <div
        class="text-sm space-y-2"
        :class="textColorClass"
    >
        <div
            v-if="date || contributors"
            class="flex gap-x-4 gap-y-2"
            :class="{ 'flex-col': isVertical }"
        >
            <div
                v-if="date"
                class="flex items-center gap-x-1.5"
            >
                <CalendarIcon
                    class="shrink-0 size-5"
                    :class="iconColorClass"
                    aria-hidden="true"
                />
                <time :datetime="date">
                    {{ useGetLocalizedDateTime(date, 'P') }}
                </time>
            </div>

            <div
                v-if="contributors && contributors.length > 0"
                class="flex items-center gap-x-1.5"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    class="shrink-0 size-5 fill-current"
                    :class="iconColorClass"
                    aria-hidden="true"
                >
                    <path
                        d="M9.75.25C4.37.25 0 4.62 0 10s4.37 9.75 9.75 9.75S19.5 15.38 19.5 10 15.13.25 9.75.25ZM6.49 17.57v-3.1c0-.63.27-1.21.72-1.62 1.4.91 3.34.91 4.74 0 .46.41.72.99.72 1.62v3.23c-.91.35-1.89.54-2.92.54-1.16 0-2.26-.24-3.26-.68v.01Zm4.72-6.67c-.88.85-2.37.85-3.26 0-.47-.45-.72-1.05-.72-1.69 0-1.3 1.05-2.35 2.35-2.35a2.347 2.347 0 0 1 1.63 4.04Zm3.46 5.71v-2.13c0-1.19-.5-2.3-1.35-3.08.39-.66.61-1.4.61-2.19 0-2.4-1.95-4.35-4.35-4.35-2.4 0-4.35 1.95-4.35 4.35 0 .79.22 1.53.61 2.19-.85.78-1.35 1.89-1.35 3.08v1.87A8.248 8.248 0 0 1 1.5 10c0-4.55 3.7-8.25 8.25-8.25S18 5.45 18 10c0 2.7-1.31 5.1-3.33 6.61Z"
                    />
                </svg>

                <span
                    class="line-clamp-1"
                    :title="contributors.map((contributor) => contributor.name).join(', ')"
                >
                    {{ contributors.map((contributor) => contributor.name).join(', ') }}
                </span>
            </div>
        </div>

        <div
            v-if="startDate"
            class="flex items-center gap-x-1.5"
        >
            <CalendarIcon
                class="shrink-0 size-5"
                :class="iconColorClass"
                aria-hidden="true"
            />

            <span class="line-clamp-1">
                {{ useGetLocalizedDateTime(startDate, 'P').substring(0, 6) }}
                {{ $t('components.card.dates.to') }}
                {{ useGetLocalizedDateTime(endDate, 'P') }}
                <span v-if="repetitionDatesCount">
                    {{ $tChoice('components.card.dates.repetitionDatesCount', { count: repetitionDatesCount }) }}
                </span>
            </span>
        </div>

        <div
            v-if="location"
            class="flex items-center gap-x-1.5"
        >
            <MapPinIcon
                class="shrink-0 size-5"
                :class="iconColorClass"
                aria-hidden="true"
            />

            <span class="line-clamp-1">
                {{ location }}
            </span>
        </div>

        <div
            v-if="topics && topics.length > 0"
            class="flex items-center gap-x-1.5"
        >
            <TagIcon
                class="shrink-0 size-5"
                :class="iconColorClass"
                aria-hidden="true"
            />

            <span
                class="line-clamp-1"
                :title="topics.map((topic) => topic.name).join(', ')"
            >
                {{ topics.map((topic) => topic.name).join(', ') }}
            </span>
        </div>
    </div>
</template>
